import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UploadSimple, Trash, PencilSimple, Triangle } from "@phosphor-icons/react";

const empty = {
  nome_marca: "", nome_empresarial: "", segmento: "", classes_inpi: [], numero_processo: "",
  titular: "", palavras_chave: [], variacoes_nome: [], termos_excluir: [], cor_principal: "", descricao_visual: ""
};

export default function Brands() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/brands");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const arrField = (val) => val.split(",").map(s => s.trim()).filter(Boolean);

  const save = async () => {
    const payload = {
      ...form,
      classes_inpi: Array.isArray(form.classes_inpi) ? form.classes_inpi : arrField(form.classes_inpi || ""),
      palavras_chave: Array.isArray(form.palavras_chave) ? form.palavras_chave : arrField(form.palavras_chave || ""),
      variacoes_nome: Array.isArray(form.variacoes_nome) ? form.variacoes_nome : arrField(form.variacoes_nome || ""),
      termos_excluir: Array.isArray(form.termos_excluir) ? form.termos_excluir : arrField(form.termos_excluir || ""),
    };
    try {
      if (editing) {
        await api.put(`/brands/${editing}`, payload);
        toast.success("Marca atualizada");
      } else {
        await api.post("/brands", payload);
        toast.success("Marca cadastrada");
      }
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (e) {
      toast.error("Falha ao salvar");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir marca?")) return;
    await api.delete(`/brands/${id}`);
    toast.success("Removida");
    load();
  };

  const editBrand = (b) => {
    setEditing(b.id);
    setForm({
      ...b,
      classes_inpi: b.classes_inpi.join(", "),
      palavras_chave: b.palavras_chave.join(", "),
      variacoes_nome: b.variacoes_nome.join(", "),
      termos_excluir: b.termos_excluir.join(", "),
    });
    setOpen(true);
  };

  const uploadLogo = async (brandId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/brands/${brandId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo enviado");
      load();
    } catch (e) { toast.error("Falha no upload"); }
  };

  const uploadVariation = async (brandId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/brands/${brandId}/logo-variations?label=${encodeURIComponent(file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Variação adicionada");
      load();
    } catch (e) { toast.error("Falha no upload"); }
  };

  const removeVariation = async (brandId, variationId) => {
    await api.delete(`/brands/${brandId}/logo-variations/${variationId}`);
    toast.success("Variação removida");
    load();
  };

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Marcas monitoradas</div>
          <h1 className="font-heading font-extrabold text-4xl">Suas marcas</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-brand-btn" className="rounded-sm bg-[#0A3B2C] hover:bg-[#083527]"><Plus size={16} className="mr-1" /> Nova marca</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Cadastrar"} marca</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label className="text-xs">Nome da marca *</Label><Input data-testid="brand-nome-input" value={form.nome_marca} onChange={(e) => setForm({ ...form, nome_marca: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Nome empresarial</Label><Input value={form.nome_empresarial} onChange={(e) => setForm({ ...form, nome_empresarial: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Segmento</Label><Input value={form.segmento} onChange={(e) => setForm({ ...form, segmento: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Titular</Label><Input value={form.titular} onChange={(e) => setForm({ ...form, titular: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Nº Processo INPI</Label><Input value={form.numero_processo} onChange={(e) => setForm({ ...form, numero_processo: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Classes INPI (vírgula)</Label><Input value={form.classes_inpi} onChange={(e) => setForm({ ...form, classes_inpi: e.target.value })} className="rounded-sm" placeholder="35, 36" /></div>
              <div className="col-span-2"><Label className="text-xs">Palavras-chave (vírgula)</Label><Input value={form.palavras_chave} onChange={(e) => setForm({ ...form, palavras_chave: e.target.value })} className="rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs">Variações do nome (vírgula)</Label><Textarea value={form.variacoes_nome} onChange={(e) => setForm({ ...form, variacoes_nome: e.target.value })} className="rounded-sm" rows={2} /></div>
              <div className="col-span-2"><Label className="text-xs">Termos a excluir (vírgula)</Label><Input value={form.termos_excluir} onChange={(e) => setForm({ ...form, termos_excluir: e.target.value })} className="rounded-sm" /></div>
              <div><Label className="text-xs">Cor principal</Label><Input value={form.cor_principal} onChange={(e) => setForm({ ...form, cor_principal: e.target.value })} className="rounded-sm" placeholder="#0A3B2C" /></div>
              <div className="col-span-2"><Label className="text-xs">Descrição visual do logo</Label><Textarea value={form.descricao_visual} onChange={(e) => setForm({ ...form, descricao_visual: e.target.value })} className="rounded-sm" rows={3} /></div>
            </div>
            <Button data-testid="save-brand-btn" onClick={save} className="rounded-sm bg-[#0A3B2C] hover:bg-[#083527]">{editing ? "Atualizar" : "Cadastrar"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <Card key={b.id} data-testid={`brand-card-${b.id}`} className="p-5 rounded-sm border card-hover">
            <div className="flex items-start justify-between mb-3">
              <div className="w-14 h-14 border rounded-sm flex items-center justify-center bg-white shrink-0 overflow-hidden">
                {b.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <Triangle size={26} weight="bold" style={{ color: b.cor_principal || "#0A3B2C" }} />
                )}
              </div>
              <div className="flex gap-1">
                <label className="p-2 hover:bg-muted cursor-pointer rounded-sm" title="Upload logo">
                  <UploadSimple size={14} />
                  <input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(b.id, e.target.files[0])} data-testid={`upload-logo-${b.id}`} />
                </label>
                <button className="p-2 hover:bg-muted rounded-sm" onClick={() => editBrand(b)}><PencilSimple size={14} /></button>
                <button className="p-2 hover:bg-muted rounded-sm text-red-600" onClick={() => del(b.id)}><Trash size={14} /></button>
              </div>
            </div>
            <div className="font-heading font-bold text-lg">{b.nome_marca}</div>
            <div className="text-xs text-muted-foreground mb-3">{b.segmento || "—"}</div>
            <div className="text-xs space-y-1">
              <div><span className="text-muted-foreground">Variações do nome:</span> {b.variacoes_nome.length}</div>
              <div><span className="text-muted-foreground">Classes INPI:</span> {b.classes_inpi.join(", ") || "—"}</div>
            </div>

            {/* Logo variations gallery */}
            <div className="mt-4 pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Variações do logotipo ({b.logo_variations?.length || 0})</div>
                <label className="p-1 hover:bg-muted cursor-pointer rounded-sm" title="Adicionar variação">
                  <Plus size={12} />
                  <input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={(e) => e.target.files[0] && uploadVariation(b.id, e.target.files[0])} data-testid={`upload-variation-${b.id}`} />
                </label>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(b.logo_variations || []).map((v) => (
                  <div key={v.id} className="relative group aspect-square border rounded-sm overflow-hidden bg-white" title={v.label}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${v.url}`} alt={v.label} className="w-full h-full object-contain p-1" />
                    <button
                      className="absolute top-0.5 right-0.5 p-0.5 bg-white/90 rounded-sm text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeVariation(b.id, v.id)}
                      title="Remover"
                      data-testid={`remove-variation-${v.id}`}
                    >
                      <Trash size={10} />
                    </button>
                  </div>
                ))}
                {(!b.logo_variations || b.logo_variations.length === 0) && (
                  <div className="col-span-4 text-[10px] text-muted-foreground py-2 text-center">Nenhuma variação anexada</div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
